import { DialogComponent, DialogOpenEvent, DialogCloseEvent } from '@theme/dialog';
import { CartAddEvent, CartUpdateEvent } from '@theme/events';
import { isMobileBreakpoint } from '@theme/utilities';

/**
 * A custom element that manages a cart drawer.
 *
 * @typedef {object} Refs
 * @property {HTMLDialogElement} dialog - The dialog element.
 * @property {HTMLElement} [liveRegion] - The live region for cart announcements when dialog is open.
 *
 * @extends {DialogComponent}
 */
class CartDrawerComponent extends DialogComponent {
  /** @type {number} */
  #summaryThreshold = 0.5;

  /** @type {AbortController | null} */
  #historyAbortController = null;

  connectedCallback() {
    super.connectedCallback();
    document.addEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.addEventListener(CartUpdateEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.addEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.addEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);

    if (history.state?.cartDrawerOpen) {
      history.replaceState(null, '');
    }
  }

  disconnectedCallback() {
    super.disconnectedCallback();
    document.removeEventListener(CartAddEvent.eventName, this.#handleCartAdd);
    document.removeEventListener(CartUpdateEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#updateStickyState);
    this.removeEventListener(DialogOpenEvent.eventName, this.#handleHistoryOpen);
    this.removeEventListener(DialogCloseEvent.eventName, this.#handleHistoryClose);
    this.#historyAbortController?.abort();
  }

  #handleHistoryOpen = () => {
    if (!isMobileBreakpoint()) return;

    if (!history.state?.cartDrawerOpen) {
      history.pushState({ cartDrawerOpen: true }, '');
    }

    this.#historyAbortController = new AbortController();
    window.addEventListener('popstate', this.#handlePopState, { signal: this.#historyAbortController.signal });
  };

  #handleHistoryClose = () => {
    this.#historyAbortController?.abort();
    if (history.state?.cartDrawerOpen) {
      history.back();
    }
  };

  #handlePopState = async () => {
    if (this.refs.dialog?.open) {
      this.refs.dialog.style.setProperty('--dialog-drawer-closing-animation', 'none');
      await this.closeDialog();
      this.refs.dialog.style.removeProperty('--dialog-drawer-closing-animation');
    }
  };

  /**
   * Handles cart add events - opens drawer if auto-open and announces count when open.
   * @param {CustomEvent<{ resource?: { item_count?: number } }>} event
   */
  #handleCartAdd = (event) => {
    if (this.hasAttribute('auto-open')) {
      this.showDialog();
    }

    this.#announceCartCount(event.detail.resource?.item_count);
  };

  /**
   * Announces cart count to screen readers when dialog is open.
   * @param {number | undefined} cartCount
   */
  #announceCartCount(cartCount) {
    const liveRegion = /** @type {HTMLElement | undefined} */ (this.refs.liveRegion);
    if (!this.refs.dialog?.open || !liveRegion || cartCount === undefined) return;

    liveRegion.textContent = `${Theme.translations.cart_count}: ${cartCount}`;
  }

  open() {
    this.showDialog();

    /**
     * Close cart drawer when installments CTA is clicked to avoid overlapping dialogs
     */
    customElements.whenDefined('shopify-payment-terms').then(() => {
      const installmentsContent = document.querySelector('shopify-payment-terms')?.shadowRoot;
      const cta = installmentsContent?.querySelector('#shopify-installments-cta');
      cta?.addEventListener('click', this.closeDialog, { once: true });
    });
  }

  close() {
    this.closeDialog();
  }

  #updateStickyState = () => {
    const { dialog } = /** @type {Refs} */ (this.refs);
    if (!dialog) return;

    // Refs do not cross nested `*-component` boundaries (e.g., `cart-items-component`), so we query within the dialog.
    const content = dialog.querySelector('.cart-drawer__content');
    const summary = dialog.querySelector('.cart-drawer__summary');

    if (!content || !summary) {
      // Ensure the dialog doesn't get stuck in "unsticky" mode when summary disappears (e.g., empty cart).
      dialog.setAttribute('cart-summary-sticky', 'false');
      return;
    }

    const drawerHeight = dialog.getBoundingClientRect().height;
    const summaryHeight = summary.getBoundingClientRect().height;
    const ratio = summaryHeight / drawerHeight;
    dialog.setAttribute('cart-summary-sticky', ratio > this.#summaryThreshold ? 'false' : 'true');
  };
}

if (!customElements.get('cart-drawer-component')) {
  customElements.define('cart-drawer-component', CartDrawerComponent);
}

/**
 * Horizontal "Complete your set" carousel inside the cart drawer.
 *
 * Expected markup:
 *   <cart-upsell-carousel>
 *     <button data-carousel-prev aria-label="...">…</button>
 *     <button data-carousel-next aria-label="...">…</button>
 *     <div data-carousel-track>
 *       <div data-carousel-slide>…</div>
 *     </div>
 *   </cart-upsell-carousel>
 *
 * Scrolling itself is native (overflow-x + scroll-snap), so the carousel
 * remains fully usable by swipe at phone width with the arrows hidden.
 */
class CartUpsellCarousel extends HTMLElement {
  /** @type {HTMLElement | null} */
  #track = null;

  /** @type {AbortController | null} */
  #abortController = null;

  connectedCallback() {
    this.#track = this.querySelector('[data-carousel-track]');
    if (!this.#track) return;

    this.#abortController = new AbortController();
    const { signal } = this.#abortController;

    this.querySelector('[data-carousel-prev]')?.addEventListener('click', () => this.#scrollBy(-1), { signal });
    this.querySelector('[data-carousel-next]')?.addEventListener('click', () => this.#scrollBy(1), { signal });
    this.#track.addEventListener('scroll', this.#updateArrows, { passive: true, signal });
    window.addEventListener('resize', this.#updateArrows, { passive: true, signal });

    this.#updateArrows();
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  /** @param {number} direction - -1 for previous, 1 for next */
  #scrollBy(direction) {
    const track = this.#track;
    if (!track) return;

    const slide = track.querySelector('[data-carousel-slide]');
    const step = slide ? slide.getBoundingClientRect().width + this.#gap() : track.clientWidth;

    track.scrollBy({ left: step * direction, behavior: 'smooth' });
  }

  #gap() {
    const track = this.#track;
    if (!track) return 0;
    return parseFloat(getComputedStyle(track).columnGap || '0') || 0;
  }

  #updateArrows = () => {
    const track = this.#track;
    if (!track) return;

    const maxScroll = track.scrollWidth - track.clientWidth;
    const atStart = track.scrollLeft <= 1;
    const atEnd = track.scrollLeft >= maxScroll - 1;

    this.#setArrowState('[data-carousel-prev]', atStart);
    this.#setArrowState('[data-carousel-next]', atEnd || maxScroll <= 0);
    this.toggleAttribute('data-scrollable', maxScroll > 0);
  };

  /**
   * @param {string} selector
   * @param {boolean} disabled
   */
  #setArrowState(selector, disabled) {
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector(selector));
    if (!button) return;

    button.disabled = disabled;
    button.setAttribute('aria-disabled', String(disabled));
  }
}

if (!customElements.get('cart-upsell-carousel')) {
  customElements.define('cart-upsell-carousel', CartUpsellCarousel);
}

/**
 * Quick-add button for upsell cards.
 *
 * Expected markup:
 *   <cart-upsell-add variant-id="123">
 *     <button type="button">Add</button>
 *   </cart-upsell-add>
 */
class CartUpsellAdd extends HTMLElement {
  /** @type {AbortController | null} */
  #abortController = null;

  /** @type {boolean} */
  #pending = false;

  connectedCallback() {
    this.#abortController = new AbortController();
    this.querySelector('button')?.addEventListener('click', this.#handleClick, {
      signal: this.#abortController.signal,
    });
  }

  disconnectedCallback() {
    this.#abortController?.abort();
  }

  #handleClick = async () => {
    const variantId = this.getAttribute('variant-id');
    if (!variantId || this.#pending) return;

    this.#setPending(true);

    try {
      const body = new FormData();
      body.append('id', variantId);
      body.append('quantity', '1');

      const response = await fetch(`${Theme.routes.cart_add_url}.js`, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });

      if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);

      const cart = await fetch(`${Theme.routes.cart_url}.js`).then((res) => res.json());

      document.dispatchEvent(
        new CartAddEvent({}, variantId, {
          source: 'cart-upsell',
          itemCount: cart.item_count,
          resource: cart,
        })
      );
    } catch (error) {
      console.error(error);
    } finally {
      this.#setPending(false);
    }
  };

  /** @param {boolean} pending */
  #setPending(pending) {
    this.#pending = pending;
    const button = /** @type {HTMLButtonElement | null} */ (this.querySelector('button'));
    if (!button) return;

    button.disabled = pending;
    this.toggleAttribute('data-pending', pending);
  }
}

if (!customElements.get('cart-upsell-add')) {
  customElements.define('cart-upsell-add', CartUpsellAdd);
}